import { c as _c } from "react/compiler-runtime";
import React from "react";
import { calendarTextScaleStyle, segmentHeaderClassName, useCalendarTextSize } from "@/lib/calendar-text-size";
import { cx } from "@/lib/tailwind-utils";
import { InlineEventMemos } from "./inline-event-notes";
export type AppointmentSegmentStackPosition = "first" | "middle" | "last" | "only";
interface AppointmentServiceSegmentCardProps extends Omit<React.ComponentPropsWithoutRef<"div">, "title"> {
  title: React.ReactNode;
  details?: React.ReactNode;
  /** Add-on names, one per line under the title row. */
  addons?: string[];
  /** Shown below the details in whatever space the card has left. */
  memos?: {
    id: string;
    note: string;
  }[];
  accentColor: string;
  position?: {
    topPct: number;
    bottomPct: number;
  };
  stackPosition?: AppointmentSegmentStackPosition;
  inactive?: boolean;
  hasIconOffset?: boolean;
}

/**
 * Caps a segment title at whole lines, leaving room under it for the time
 * row (one `text-calendar-micro` line plus the stacked gap), so a long title never
 * pushes the time out of a short card or shows a line cut through the middle.
 */
export const SEGMENT_TITLE_STYLE: React.CSSProperties = {
  maxHeight: "max(1lh, round(down, 100cqh - 0.625rem * var(--calendar-font-scale, 1) - 1px, 1lh))"
};

/** Shared visual chrome for service segments in the calendar and its previews. */
export function AppointmentServiceSegmentCard(t0) {
  const $ = _c(61);
  let accentColor;
  let addons;
  let className;
  let details;
  let memos;
  let position;
  let props;
  let style;
  let t1;
  let t2;
  let t3;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      details,
      addons,
      memos,
      accentColor,
      position,
      stackPosition: t1,
      inactive: t2,
      hasIconOffset: t3,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = accentColor;
    $[2] = addons;
    $[3] = className;
    $[4] = details;
    $[5] = memos;
    $[6] = position;
    $[7] = props;
    $[8] = style;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = title;
  } else {
    accentColor = $[1];
    addons = $[2];
    className = $[3];
    details = $[4];
    memos = $[5];
    position = $[6];
    props = $[7];
    style = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    title = $[12];
  }
  const stackPosition = t1 === undefined ? "only" : t1;
  const inactive = t2 === undefined ? false : t2;
  const hasIconOffset = t3 === undefined ? false : t3;
  const textSize = useCalendarTextSize();
  let t4;
  if ($[13] !== textSize) {
    t4 = calendarTextScaleStyle(textSize);
    $[13] = textSize;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== position) {
    t5 = position ? {
      top: `${position.topPct}%`,
      bottom: `${position.bottomPct}%`,
      left: 0,
      width: "100%"
    } : undefined;
    $[15] = position;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let segmentStyle;
  if ($[17] !== stackPosition || $[18] !== style || $[19] !== t4 || $[20] !== t5) {
    segmentStyle = {
      ...t4,
      ...style,
      ...t5
    };
    if (stackPosition === "middle" || stackPosition === "last") {
      segmentStyle.borderTopWidth = 0;
    }
    $[17] = stackPosition;
    $[18] = style;
    $[19] = t4;
    $[20] = t5;
    $[21] = segmentStyle;
  } else {
    segmentStyle = $[21];
  }
  const t6 = position && "absolute [container-type:size]";
  const t7 = stackPosition === "only" ? "rounded-md" : stackPosition === "first" ? "rounded-t-md" : stackPosition === "last" ? "rounded-b-md" : "";
  const t8 = inactive && "!line-through";
  let t9;
  if ($[22] !== className || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t9 = cx("pointer-events-auto overflow-hidden", t6, t7, t8, className);
    $[22] = className;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = stackPosition === "only" ? "rounded-l-md" : stackPosition === "first" ? "rounded-tl-md" : stackPosition === "last" ? "rounded-bl-md" : "";
  let t11;
  if ($[27] !== t10) {
    t11 = cx("flex h-full border-l-[3px]", t10);
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== accentColor) {
    t12 = {
      borderLeftColor: accentColor
    };
    $[29] = accentColor;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const t13 = hasIconOffset ? "16px" : "0";
  let t14;
  if ($[31] !== t13) {
    t14 = {
      paddingLeft: t13
    };
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== textSize) {
    t15 = segmentHeaderClassName(textSize);
    $[33] = textSize;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== title) {
    t16 = <div className="overflow-hidden text-calendar-tiny font-medium leading-tight" style={SEGMENT_TITLE_STYLE}>{title}</div>;
    $[35] = title;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== details) {
    t17 = details ? <div className="text-calendar-micro opacity-80">{details}</div> : null;
    $[37] = details;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t16 || $[41] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== addons) {
    t19 = addons && addons.length > 0 ? <div className="mt-0.5 flex flex-col text-calendar-micro font-medium leading-tight">{addons.map(_temp)}</div> : null;
    $[43] = addons;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== memos) {
    t20 = memos && memos.length > 0 ? <InlineEventMemos memos={memos} className="mt-0.5" /> : null;
    $[45] = memos;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t14 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20) {
    t21 = <div className="ml-1 flex min-w-0 flex-1 flex-col" style={t14}>{t18}{t19}{t20}</div>;
    $[47] = t14;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t11 || $[53] !== t12 || $[54] !== t21) {
    t22 = <div className={t11} style={t12}>{t21}</div>;
    $[52] = t11;
    $[53] = t12;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== props || $[57] !== segmentStyle || $[58] !== t22 || $[59] !== t9) {
    t23 = <div {...props} className={t9} style={segmentStyle}>{t22}</div>;
    $[56] = props;
    $[57] = segmentStyle;
    $[58] = t22;
    $[59] = t9;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  return t23;
}
function _temp(name, index) {
  return <span key={`${name}-${index}`}>{`+ ${name}`}</span>;
}